import api from '../services/api';
import { DIRECT_UPLOAD_MAX_SIZE } from '../constants/uploadLimits';

const UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;

const guessContentType = (file) => {
  if (file.type) return file.type;
  const ext = file.name.toLowerCase().match(/\.[^.]+$/)?.[0];
  if (ext === '.csv') return 'text/csv';
  if (ext === '.xls') return 'application/vnd.ms-excel';
  if (ext === '.xlsx') return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  return 'application/octet-stream';
};

const uploadViaGcs = async (file, { companyId, name, sheetName }) => {
  const contentType = guessContentType(file);

  const initResponse = await api.post('/data-sources/upload/init', {
    fileName: file.name,
    contentType,
    fileSize: file.size,
    companyId
  });

  const { uploadId, signedUrl, contentType: signedContentType } = initResponse.data;

  const putResponse = await fetch(signedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': signedContentType || contentType
    }
  });

  if (!putResponse.ok) {
    throw new Error(`Direct upload failed (${putResponse.status}). Check GCS bucket CORS configuration.`);
  }

  return api.post('/data-sources/upload/complete', {
    uploadId,
    name: name || file.name,
    companyId,
    ...(sheetName ? { sheetName } : {})
  }, {
    timeout: UPLOAD_TIMEOUT_MS
  });
};

const uploadViaApi = async (file, { companyId, name, sheetName }) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('name', name || file.name);
  formData.append('companyId', companyId);
  if (sheetName) {
    formData.append('sheetName', sheetName);
  }

  return api.post('/data-sources/upload', formData, {
    headers: { 'Content-Type': undefined },
    timeout: UPLOAD_TIMEOUT_MS
  });
};

/**
 * Upload a CSV/Excel file using direct GCS upload for large files (Cloud Run body limit ~32MB).
 */
export const uploadDataSourceFile = async (file, options = {}) => {
  const { companyId, name, sheetName } = options;

  if (file.size > DIRECT_UPLOAD_MAX_SIZE) {
    return uploadViaGcs(file, { companyId, name, sheetName });
  }

  return uploadViaApi(file, { companyId, name, sheetName });
};

export const shouldUseDirectUpload = (file) => file.size > DIRECT_UPLOAD_MAX_SIZE;
